import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { propertyUnitAPI, propertyLedgerAPI } from '../../services/api';
import RecordFormModal from '../Common/admin/RecordFormModal';
import { saveAs } from 'file-saver';
import { apiErrorMessage } from '../../utils/apiError';
import Modal from '../Common/admin/Modal';

// Current year plus the two before it.
const YEARS = (() => {
  const now = new Date().getFullYear();
  return [now, now - 1, now - 2];
})();

const money = (value) =>
  value === null || value === undefined
    ? '-'
    : Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const day = (value) => (value ? String(value).slice(0, 10) : '-');

// Paid reads as success, a shortfall as danger, an empty month as neutral.
const STATUS_TONE = {
  paid: 'ad-badge-success',
  partial: 'ad-badge-danger',
  unpaid: 'ad-badge-danger',
  notRented: '',
};

const STATUSES = ['unpaid', 'paid', 'partial', 'notRented'];

/**
 * "مسير العقار" — pick a year, then a unit, and read that unit's rent ledger.
 *
 * Two narrow sidebars keep the report itself as wide as possible.
 */
const PropertyLedgerModal = ({ show, onClose, property }) => {
  const { t } = useTranslation();
  const { can } = useAuth();
  const [year, setYear] = useState(YEARS[0]);
  const [units, setUnits] = useState([]);
  const [unitId, setUnitId] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failure, setFailure] = useState(null);
  const [exporting, setExporting] = useState(false);
  // The row whose part payment is being entered.
  const [partial, setPartial] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const editable = can('contracts', 'Edit');

  useEffect(() => {
    if (!show || !property?.id) return;
    setYear(YEARS[0]);
    setUnitId(null);
    setLedger(null);
    setFailure(null);

    propertyUnitAPI
      .getByProperty(property.id)
      .then(({ data }) => {
        setUnits(data || []);
        if (data?.length) setUnitId(data[0].id);
      })
      .catch((err) => setFailure(apiErrorMessage(err)));
  }, [show, property?.id]);

  const load = useCallback(() => {
    if (!unitId) return;
    setLoading(true);
    setFailure(null);
    propertyLedgerAPI
      .getUnitLedger(unitId, year)
      .then(({ data }) => setLedger(data))
      .catch((err) => setFailure(apiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [unitId, year]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = ledger?.rows || [];

  const saveStatus = async (row, status, paidAmount = null) => {
    setSavingId(row.instalmentId);
    setFailure(null);
    try {
      await propertyLedgerAPI.updateInstalment(row.instalmentId, {
        status,
        paidAmount,
        paidDate: row.transferDate || null,
        voucherNumber: row.voucherNumber || null,
        paymentMethod: row.paymentMethod || null,
      });
      load();
    } catch (err) {
      setFailure(apiErrorMessage(err));
    } finally {
      setSavingId(null);
    }
  };

  // A part payment needs a figure, so it asks before saving; the rest are implied.
  const changeStatus = (row, status) =>
    status === 'partial' ? setPartial(row) : saveStatus(row, status);

  const partialFields = [
    {
      name: 'paidAmount',
      label: t('property.ledger.columns.paid'),
      placeholder: t('property.ledger.columns.paid'),
      icon: 'fa-sack-dollar',
      type: 'number',
      required: true,
    },
    {
      name: 'transferDate',
      label: t('property.ledger.columns.transferDate'),
      icon: 'fa-calendar-day',
      type: 'date',
    },
    {
      name: 'voucherNumber',
      label: t('property.ledger.columns.voucher'),
      placeholder: t('property.ledger.columns.voucher'),
      icon: 'fa-hashtag',
      type: 'text',
    },
  ];

  const savePartial = async (values) => {
    await propertyLedgerAPI.updateInstalment(partial.instalmentId, {
      status: 'partial',
      paidAmount: Number(values.paidAmount),
      paidDate: values.transferDate || null,
      voucherNumber: values.voucherNumber || null,
      paymentMethod: partial.paymentMethod || null,
    });
    setPartial(null);
    load();
  };

  const exportExcel = async () => {
    if (!unitId) return;
    setExporting(true);
    setFailure(null);
    try {
      const { data } = await propertyLedgerAPI.exportExcel(unitId, year);
      const unitName = ledger?.unit?.unitNumber || unitId;
      saveAs(new Blob([data]), `Ledger_${unitName}_${year}.xlsx`);
    } catch (err) {
      setFailure(apiErrorMessage(err));
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
    <Modal
      show={show}
      title={t('property.ledger.title')}
      subtitle={property?.label}
      size="wide"
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="ad-btn ad-btn-ghost"
            onClick={exportExcel}
            disabled={!unitId || exporting || loading}
          >
            <i className="fas fa-file-excel"></i>
            {exporting ? t('common.loading') : t('common.exportExcel')}
          </button>
          <button type="button" className="ad-btn" onClick={onClose}>
            {t('common.close')}
          </button>
        </>
      }
    >
      <div className="ad-ledger">
        {/* years */}
        <aside className="ad-ledger-side">
          <h4 className="ad-ledger-side-title">{t('property.ledger.years')}</h4>
          {YEARS.map((value) => (
            <button
              key={value}
              type="button"
              className={`ad-ledger-item ${value === year ? 'ad-ledger-item-active' : ''}`}
              onClick={() => setYear(value)}
            >
              <i className="fas fa-calendar"></i>
              {value}
            </button>
          ))}
        </aside>

        {/* units */}
        <aside className="ad-ledger-side">
          <h4 className="ad-ledger-side-title">{t('property.ledger.units')}</h4>
          {units.length === 0 && <p className="ad-hint">{t('tree.empty')}</p>}
          {units.map((unit) => (
            <button
              key={unit.id}
              type="button"
              className={`ad-ledger-item ${unit.id === unitId ? 'ad-ledger-item-active' : ''}`}
              onClick={() => setUnitId(unit.id)}
            >
              <i className="fas fa-door-open"></i>
              {unit.unitNumber || `#${unit.id}`}
            </button>
          ))}
        </aside>

        {/* report */}
        <section className="ad-ledger-report">
          {failure && <p className="ad-error-text">{failure}</p>}
          {loading && <p className="ad-column-note">{t('tree.loading')}</p>}

          {!loading && ledger && (
            <>
              <div className="ad-ledger-head">
                <span>
                  {t('property.ledger.head.unit')}: <strong>{ledger.unit.unitNumber || '-'}</strong>
                </span>
                <span>
                  {t('property.ledger.head.electricity')}:{' '}
                  <strong>{ledger.unit.electricityMeter || '-'}</strong>
                </span>
                <span>
                  {t('property.ledger.head.water')}: <strong>{ledger.unit.waterMeter || '-'}</strong>
                </span>
                <span>
                  {t('property.ledger.head.contractDate')}:{' '}
                  <strong>{day(ledger.contractDate)}</strong>
                </span>
                <span>
                  {t('property.ledger.head.contractStatus')}:{' '}
                  <strong>{t(`property.ledger.status.${ledger.contractStatus}`)}</strong>
                </span>
                <span>
                  {t('property.ledger.head.carryOver')}:{' '}
                  <strong>{money(ledger.totals.carryOver)}</strong>
                </span>
                <span>
                  {t('property.ledger.head.totalDebt')}:{' '}
                  <strong className={ledger.totals.totalDebt > 0 ? 'ad-ledger-due' : ''}>
                    {money(ledger.totals.totalDebt)}
                  </strong>
                </span>
              </div>

              <div className="ad-ledger-scroll">
                <table className="ad-ledger-table">
                  <thead>
                    <tr>
                      <th>{t('property.ledger.columns.dueDate')}</th>
                      <th>{t('property.ledger.columns.tenant')}</th>
                      <th>{t('property.ledger.columns.mobile')}</th>
                      <th>{t('property.ledger.columns.periodFrom')}</th>
                      <th>{t('property.ledger.columns.annualRent')}</th>
                      <th>{t('property.ledger.columns.frequency')}</th>
                      <th>{t('property.ledger.columns.monthly')}</th>
                      <th>{t('property.ledger.columns.amount')}</th>
                      <th>{t('property.ledger.columns.paid')}</th>
                      <th>{t('property.ledger.columns.remaining')}</th>
                      <th>{t('property.ledger.columns.voucher')}</th>
                      <th>{t('property.ledger.columns.transferDate')}</th>
                      <th>{t('property.ledger.columns.paymentMethod')}</th>
                      <th>{t('property.ledger.columns.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 && (
                      <tr>
                        <td colSpan="14" className="ad-ledger-empty">
                          {t('property.ledger.noRows')}
                        </td>
                      </tr>
                    )}
                    {rows.map((row, index) => {
                      const vacant = row.status === 'notRented';
                      return (
                        <tr
                          key={`${row.dueDate}-${row.contractId ?? 'x'}-${index}`}
                          className={vacant ? 'ad-ledger-vacant' : ''}
                        >
                          <td>{day(row.dueDate)}</td>
                          <td>{row.tenantName || '-'}</td>
                          <td>{row.tenantMobile || '-'}</td>
                          <td>{day(row.periodFrom)}</td>
                          <td>{vacant ? '-' : money(row.annualRent)}</td>
                          <td>
                            {row.paymentFrequency
                              ? t(`contract.intake.frequencies.${row.paymentFrequency}`)
                              : '-'}
                          </td>
                          <td>{vacant ? '-' : money(row.monthlyInstalment)}</td>
                          <td>{vacant ? '-' : money(row.amount)}</td>
                          <td>{vacant ? '-' : money(row.paid)}</td>
                          <td className={row.remaining > 0 ? 'ad-ledger-due' : ''}>
                            {vacant ? '-' : money(row.remaining)}
                          </td>
                          <td>{row.voucherNumber || '-'}</td>
                          <td>{day(row.transferDate)}</td>
                          <td>{row.paymentMethod || '-'}</td>
                          <td>
                            {/* A vacant month has no instalment to record against. */}
                            {editable && row.instalmentId ? (
                              <select
                                className="ad-input-sm ad-ledger-status"
                                value={row.status}
                                disabled={savingId === row.instalmentId}
                                onChange={(e) => changeStatus(row, e.target.value)}
                              >
                                {STATUSES.map((status) => (
                                  <option key={status} value={status}>
                                    {t(`property.ledger.status.${status}`)}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <span className={`ad-badge ${STATUS_TONE[row.status] || ''}`}>
                                {t(`property.ledger.status.${row.status}`)}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {rows.length > 0 && (
                    <tfoot>
                      <tr>
                        <td colSpan="7">{t('property.ledger.totals')}</td>
                        <td>{money(ledger.totals.due)}</td>
                        <td>{money(ledger.totals.paid)}</td>
                        <td className={ledger.totals.remaining > 0 ? 'ad-ledger-due' : ''}>
                          {money(ledger.totals.remaining)}
                        </td>
                        <td colSpan="4"></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </>
          )}
        </section>
      </div>
    </Modal>

      <RecordFormModal
        show={partial !== null}
        title={t('property.ledger.status.partial')}
        fields={partialFields}
        initial={
          partial && {
            paidAmount: partial.paid || '',
            transferDate: partial.transferDate || '',
            voucherNumber: partial.voucherNumber || '',
          }
        }
        onSubmit={savePartial}
        onClose={() => setPartial(null)}
      />
    </>
  );
};

export default PropertyLedgerModal;
